import React, { useState, useContext, useCallback, useRef, useEffect, useMemo } from "react";
import { LassieContext } from "../../context/LassieProvider";
import { defectAPI, inspectionAPI } from "../../api";
import TurbineLightningRuler from "../turbine-lightning-ruler/TurbineLightningRuler";
import HeaderInfo from "../header-info/HeaderInfo";
import { toPrettyDateStr, getInspectionDate, toStandardWplTimestamp, formatInspectionDateToYMD, toZonedDateStr, windfarmTimeLabel, windfarmTimeZone } from "../../prettyDate";
import { generateRandomId } from "../../consist/generateRandomId";
import UserActionsWplFileUpload from "../../components/user-action-wpl-file-upload/userActionWplFileUpload";
import WplInput from "../wpl-input/WplInput";
import { getIconForTurbine } from "../ol-map/icons";
import redirectIcon from "../../assets/img/goto.png";
import blackLightning from "../../assets/black_lightning.svg";
import LoaderAnimation from '../../assets/img/loader-spinner.gif'

export default function MapPopup({
  showLightningTable,
  setShowLightningTable,
  showSensorTable,
  setShowSensorTable
}) {
  const { createUserAction, organization, getInspectionTemplates, inspectionTemplates, handleResolveInspection, handleScheduleInspections, handleUploadInspectionData, handleCreateDisposition, selectedTurbine, mapPopUpLoading, fetchOrganizations, setMapPopUpLoading, feedback, setFeedback, layerSettings } = useContext(LassieContext);
  // Strike and sensor times are on the wind farm's clock, not the viewer's - the turbine's own
  // position is enough to tell which clock that is.
  const timeZone = useMemo(
    () => windfarmTimeZone(selectedTurbine ? [selectedTurbine] : []),
    [selectedTurbine]
  );
  const timeLabel = windfarmTimeLabel(timeZone);
  const [hoveredStrike, setHoveredStrike] = useState(null);
  const [sortField, setSortField] = useState("distance_km");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [showAddAction, setShowAddAction] = useState({ status: false, layout: "" });
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [isTemplateComment, setIsTemplateComment] = useState(false);
  const [userComment, setUserComment] = useState("");
  const [targetDate, setTargetDate] = useState(toPrettyDateStr(new Date()));
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateIndex, setTemplateIndex] = useState(0);
  const [newDefect, setNewDefect] = useState({ defect_type: null, distance: null, layer: null, severity: null, blade: null, surface: null, repair_recommendation: null, report_defect_id: generateRandomId(), defect_id: null, is_internal: false });
  const defect = { blade: ["A", "B", "C"], surface: ["LE", "SS", "TE", "PS"], severity: ["1", "2", "3", "4", "5"] }
  const [inspectionOptions, setInspectionOptions] = useState({
    inspection_type: null,
    inspection_method: null,
  });
  const [inspections, setInspections] = useState([])
  const [selectedInspectionId, setSelectedInspectionId] = useState(null);


  const prevTurbineId = useRef(null);

  useEffect(() => {
    if (!organization || organization.length === 0) {
      fetchOrganizations();
    }
  }, []);

  useEffect(() => {
    if (!organization?.id) return;
    if (!inspectionTemplates.length) {
      getInspectionTemplates(organization.id);
    }
  }, [organization]);

  useEffect(() => {
    if (!selectedTurbine.id) return
    fetchInspections()
    if (selectedTurbine.inspection?.status !== 'completed' || selectedTurbine.inspection?.status !== 'rejected') {
      setSelectedInspectionId(selectedTurbine.inspection?.id || null)
    }
  }, [selectedTurbine])

  useEffect(() => {
    const resetAll = () => {
      setShowAddAction(false);
      setUserComment('');
      setIsTemplateComment(false);
      setTargetDate(toPrettyDateStr(new Date()));
      setUploadedDocuments({});
      resetForm();
    };

    if (!selectedTurbine) {
      resetAll();
      prevTurbineId.current = null;
      return;
    }

    if (selectedTurbine.id !== prevTurbineId.current) {
      resetAll();
      prevTurbineId.current = selectedTurbine.id;
    }
  }, [selectedTurbine]);

  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => {
        setFeedback({ type: "", message: "" });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const currentTemplate =
    inspectionTemplates.length > 0
      ? inspectionTemplates[templateIndex]
      : { title: "No templates to show", comment: "Comment templates can be set up in the organization settings." };

  const handleStrikeHover = useCallback((strike) => {
    setHoveredStrike(strike);
  }, []);


  const fetchInspections = useCallback(async () => {
    setMapPopUpLoading(true)
    try {
      const res = await inspectionAPI.listInspections(selectedTurbine.id)
      setInspections(res)
    } catch (error) {
      console.error(`There was an error fetching inspections: ${error}`)
    }
    finally {
      setMapPopUpLoading(false)
    }
  })


  const datePickerRef = useRef();
  const openDatePicker = useCallback(() => {
    if (!datePickerRef.current) return;
    datePickerRef.current.showPicker();
  }, [datePickerRef]);

  const handleNameClick = () => {
    window.open(
      `/lassie/windfarm/${selectedTurbine.windfarm_id}/turbine/${selectedTurbine.id}`,
      "_blank"
    );
  };

  const uploadedFile = useCallback((filename, data) => {
    setUploadedDocuments(prev => ({ ...prev, [filename]: data }));
  }, []);

  const deletedFile = useCallback((filename) => {
    setUploadedDocuments(prev => {
      const copy = { ...prev };
      delete copy[filename];
      return copy;
    });
  }, []);

  const submitUserAction = (action) => {
    createUserAction({
      windfarm_id: selectedTurbine.windfarm_id,
      turbine_ids: [selectedTurbine.id],
      action: action,
      target_date: targetDate,
      user_comment: userComment,
      file_names: Object.keys(uploadedDocuments),
      file_datas: Object.keys(uploadedDocuments).map(
        (k) => uploadedDocuments[k]
      ),
      due_in_hours: selectedTemplate?.due_in_hours || null,
      inspection_type: selectedTemplate?.inspection_type || null,
      inspection_method: selectedTemplate?.inspection_method || null,
    });
  };

  const createDefect = async ({ turbine_id, defect, image_source, defect_image_data, blade_image_data }) => {
    try {
      const result = await defectAPI.createDefect({ turbine_id, defect, image_source, defect_image_data, blade_image_data });
      return result;
    } catch (error) {
      console.error("Error creating defect:", error);
      throw error;
    }
  };

  const handleCreateDefect = async ({
    turbine_id,
    inspection_id,
    user_comment,
    image_source,
    defect_image_data,
    blade_image_data
  }) => {
    setMapPopUpLoading(true)
    const defect = newDefect;
    try {
      const createdDefectId = await createDefect({ turbine_id, defect, image_source, defect_image_data, blade_image_data });

      if (!createdDefectId) {
        throw new Error("Failed to create defect or defect id is missing");
      }

      setNewDefect((prev) => ({ ...prev, defect_id: createdDefectId }));

      await handleCreateDisposition({
        turbine_id: turbine_id,
        inspection_id: inspection_id,
        defect_id: createdDefectId,
        user_comment: user_comment
      });
    }
    catch (error) {
      console.error("Error creating defect and disposition:", error);
    } finally {
      setMapPopUpLoading(false)
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortOrder("ASC");
    }
  };

  const sortStrikes = useCallback(
    (a, b) => {
      let result = 0;
      switch (sortField) {
        case "distance_km":
          result = a.distance_km - b.distance_km;
          break;
        case "probability_of_hit":
          result = a.probability_of_hit - b.probability_of_hit;
          break;
        case "peak_current":
          result = a.peak_current - b.peak_current;
          break;
        case "timestamp":
          result = new Date(a.timestamp) - new Date(b.timestamp);
          break;
        default:
          result = 0;
      }
      return sortOrder === "ASC" ? result : -result;
    },
    [sortField, sortOrder]
  );

  const handleCommentTypeSelector = () => {
    const switchingToTemplate = !isTemplateComment;
    setIsTemplateComment(switchingToTemplate);

    if (switchingToTemplate && inspectionTemplates.length > 0) {
      const newTemplate = inspectionTemplates[templateIndex];
      setUserComment(newTemplate.comment);
      setSelectedTemplate(newTemplate);
    } else {
      setUserComment("");
      setSelectedTemplate({});
    }
  };

  function handleNext() {
    if (!inspectionTemplates.length) return;

    const nextIndex = (templateIndex + 1) % inspectionTemplates.length;
    setTemplateIndex(nextIndex);
    const newTemplate = inspectionTemplates[nextIndex];
    setUserComment(newTemplate?.comment || '');
    setSelectedTemplate(newTemplate);
  }

  function handlePrev() {
    if (!inspectionTemplates.length) return;

    const prevIndex = (templateIndex - 1 + inspectionTemplates.length) % inspectionTemplates.length;
    setTemplateIndex(prevIndex);
    const newTemplate = inspectionTemplates[prevIndex];
    setUserComment(newTemplate?.comment || '');
    setSelectedTemplate(newTemplate);
  }

  const resetForm = () => {
    setUserComment('')
    setIsTemplateComment(false);
    setUploadedDocuments({});
    setTargetDate(toPrettyDateStr(new Date()));
    setNewDefect({ defect_type: null, distance: null, layer: null, severity: null, blade: null, surface: null, repair_recommendation: null, report_defect_id: generateRandomId(), defect_id: null, is_internal: false })
  }

  const requiredFields = [
    "defect_type",
    "distance",
    "layer",
    "severity",
    "blade",
    "surface",
    "repair_recommendation"
  ];

  const isDefectIncomplete = requiredFields.some(field => {
    const value = newDefect[field];
    return value === null || value === "";
  });

  const getPopupGuideMessage = () => {
    if (selectedTurbine.inspection === null && showAddAction.layout !== 'comment') {
      return "The selected turbine has no inspection data attached..."
    }
    if (showAddAction.layout === 'inspection_report' && Object.keys(uploadedDocuments).length === 0) {
      return "Upload attachment is required for uploading inspection data...";
    }
    if (showAddAction.layout === 'disposition' && isDefectIncomplete) {
      return "Please complete all defect fields before creating a defect..."
    }
    if (showAddAction.layout === 'disposition' && !isDefectIncomplete && Object.keys(uploadedDocuments).length === 0) {
      return "Upload attachment is required for creating a defect...";
    }
    return '';
  };

  const guideMessage = getPopupGuideMessage();

  return (
    <>
      {selectedTurbine && (
        <div className={`selected-turbine ${!showAddAction ? 'lightning-data' : 'action-form'}`}>
          <div className="title-wrapper">
            <div
              className="turbine-name-pill"
              data-full-name={
                selectedTurbine.name.length > 7 ? selectedTurbine.name : null
              }
              onClick={() => handleNameClick()}
            >
              <p>{selectedTurbine.name}</p>
              <img src={redirectIcon} className="popup-icon" />
            </div>

            {showAddAction && <div className="turbine-name-pill"
              onClick={() => {
                setShowAddAction(false);
                resetForm()
              }}>
              <p>
                Lightning data
              </p>
              <img src={blackLightning} className="lightning-icon" /></div>}
            <div className="status-icon-wrapper">
              {layerSettings.view === "Defect" &&
                !showAddAction.status &&
                !!selectedTurbine.marked_for_repair && (
                  <p>Turbine marked for repair</p>
                )}

              <img
                className="turbine-status-icon"
                title={`Turbine ${selectedTurbine.status.replace(/_/g, " ")}`}
                src={getIconForTurbine(
                  selectedTurbine,
                  layerSettings.most_probable,
                )}
                alt={selectedTurbine.status}
              />

              {!showAddAction.status && selectedTurbine?.inspection?.due_date && ['pending', 'scheduled', 'overdue'].includes(selectedTurbine?.inspection?.status) &&
                (
                  <div>
                    {selectedTurbine?.inspection?.method && <p>
                      {selectedTurbine.inspection?.method} inspection
                    </p>}
                    <p>
                      {!selectedTurbine?.inspection?.method ? "Inspection " : null}
                      {getInspectionDate(
                        new Date(selectedTurbine.inspection.due_date)
                      )}
                    </p>
                  </div>
                )}
            </div>
          </div>

          <div className="popup-guide-container">
            {!mapPopUpLoading && !feedback.message && guideMessage && (
              <p className="guide-message hint">{guideMessage}</p>
            )}

            {mapPopUpLoading && (
              <div className="guide-message-loading">
                <img src={LoaderAnimation} alt="Loading spinner" style={{ width: 40, height: 40 }} />
              </div>
            )}

            {!mapPopUpLoading && feedback.message && (
              <p className={`guide-message ${feedback.type === "error" ? "error" : "success"}`}>
                {feedback.message}
              </p>
            )}

            {!guideMessage && !mapPopUpLoading && !feedback.message && (
              <div className="guide-message-spacer"></div>
            )}
          </div>

          {!showAddAction.status &&
            <>
              <div className="ruler-container">
                <TurbineLightningRuler
                  strikes={selectedTurbine.turbine_lightning
                    .filter(
                      (tl) => !layerSettings.most_probable || tl.most_probable
                    )
                    .filter(
                      (tl) =>
                        !layerSettings.only_show_outside_iec ||
                        tl.outside_certification
                    )}
                  onStrikeHover={handleStrikeHover}
                  onMouseLeave={() => setHoveredStrike(null)}
                />
              </div>

              <div className="map-pop-up-table-container">
                <div
                  className={`table-scroll ${selectedTurbine.ping_measurements?.length > 0 &&
                    showLightningTable
                    ? "sensor-table"
                    : "full-height-table"
                    }`}
                >
                  <table className="styled-table">
                    <thead>
                      <tr>
                        <th
                          className="cursor-pointer"
                          onClick={() => toggleSort("distance_km")}
                        >
                          Distance{" "}
                          {sortField === "distance_km"
                            ? sortOrder === "ASC"
                              ? "↓"
                              : "↑"
                            : "↑↓"}
                        </th>
                        <th
                          className="cursor-pointer"
                          onClick={() => toggleSort("probability_of_hit")}
                        >
                          probability{" "}
                          {sortField === "probability_of_hit"
                            ? sortOrder === "ASC"
                              ? "↓"
                              : "↑"
                            : "↑↓"}
                        </th>
                        <th
                          className="cursor-pointer"
                          onClick={() => toggleSort("peak_current")}
                        >
                          Peak{" "}
                          {sortField === "peak_current"
                            ? sortOrder === "ASC"
                              ? "↓"
                              : "↑"
                            : "↑↓"}
                        </th>
                        <th>IEC</th>
                        <th>Subsequent stroke</th>
                        <th
                          className="cursor-pointer"
                          onClick={() => toggleSort("timestamp")}
                        >
                          Date ({timeLabel.name}){" "}
                          <HeaderInfo text={timeLabel.note} />
                          {sortField === "timestamp"
                            ? sortOrder === "ASC"
                              ? "↓"
                              : "↑"
                            : "↑↓"}
                        </th>
                        {selectedTurbine.ping_measurements?.length > 0 && (
                          <th>
                            <div
                              onClick={() => setShowLightningTable((prev) => !prev)}
                              className={`pop-up-arrow-icon ${showLightningTable ? "open" : "closed"
                                }`}
                            ></div>
                          </th>
                        )}
                      </tr>
                    </thead>
                    {showLightningTable && (
                      <tbody>
                        {[...selectedTurbine.turbine_lightning]
                          .filter(
                            (tl) => !layerSettings.most_probable || tl.most_probable
                          )
                          .filter(
                            (tl) =>
                              !layerSettings.only_show_outside_iec ||
                              tl.outside_certification
                          )
                          .sort(sortStrikes)
                          .map((tl) => (
                            <tr
                              className={`popup-row ${tl.outside_certification ? "outside-cert" : ""
                                } ${hoveredStrike && hoveredStrike.id === tl.id
                                  ? "highlighted"
                                  : ""
                                }`}
                              key={tl.id}
                            >
                              <td>{Math.round(tl.distance_km * 100) * 10} m</td>
                              <td>{Math.round(tl.probability_of_hit * 100)} %</td>
                              <td>{Math.round(tl.peak_current * 10) / 10} kA</td>
                              <td>{tl.outside_certification ? "✔" : ""}</td>
                              <td>{tl.parent_id !== null ? "✔" : " "}</td>
                              <td>
                                {toZonedDateStr(new Date(tl.timestamp), timeZone, true)}
                              </td>
                              {selectedTurbine.ping_measurements?.length > 0 && (
                                <td></td>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    )}
                  </table>
                </div>

                {selectedTurbine.ping_measurements?.length > 0 && (
                  <div
                    className={`table-scroll ${showSensorTable && "sensor-table"} `}
                  >
                    <table className="styled-table">
                      <thead>
                        <tr>
                          <th>
                            Sensor measurement ({timeLabel.name}){" "}
                            <HeaderInfo text={timeLabel.note} />
                          </th>
                          <th>
                            <div
                              onClick={() => setShowSensorTable((prev) => !prev)}
                              className={`pop-up-arrow-icon ${showSensorTable ? "open" : "closed"
                                }`}
                            ></div>
                          </th>
                        </tr>
                      </thead>
                      {showSensorTable && (
                        <tbody>
                          {selectedTurbine.ping_measurements.map((pm, i) => (
                            <tr key={i} className="popup-row">
                              <td>{toZonedDateStr(new Date(pm), timeZone, true)}</td>
                              <td></td>
                            </tr>
                          ))}
                        </tbody>
                      )}
                    </table>
                  </div>
                )}
              </div>
            </>
          }

          {inspections && showAddAction.status && (showAddAction.layout === 'inspection_report' || showAddAction.layout === 'disposition') &&
            <span className="inspection-select-container inspection-section">
              <label className="inspection-selector-label">Select inspection</label>
              <select
                value={selectedInspectionId || ""}
                onChange={(e) => setSelectedInspectionId(e.target.value)}
              >
                {inspections.map(inspection => {
                  return (
                    <option key={inspection.id} value={inspection.id}>
                      {[
                        formatInspectionDateToYMD(inspection.timestamp_created),
                        inspection.status,
                        inspection.type,
                        inspection.method
                      ]
                        .filter(Boolean)
                        .join(" - ")}
                    </option>
                  );
                })}
              </select>
            </span>
          }

          {showAddAction.status && (showAddAction.layout === 'comment' || showAddAction.layout === 'inspection_report' || showAddAction.layout === 'schedule_inspection') &&
            <div className="action-form-container">
              <div className="template-date-selector-header">
                <div className="cursor-pointer">
                  <div
                    className="comment-type-selector"
                    onClick={() => handleCommentTypeSelector()}
                  >
                    <div
                      className={`comment-slider ${isTemplateComment ? "right" : "left"}`}
                    />
                    <p className={`comment-label ${!isTemplateComment ? "active" : ""}`}>
                      Add a comment
                    </p>
                    <p className={`comment-label ${isTemplateComment ? "active" : ""}`}>
                      Use a template
                    </p>
                  </div>
                </div>


                <div className="date-selection-wrapper">
                  <WplInput
                    inputRef={datePickerRef}
                    onClick={openDatePicker}
                    className="performed-inspection-date"
                    type="date"
                    value={targetDate}
                    onChanged={(e) => setTargetDate(e)}
                  />
                </div>
              </div>

              <div className="attachments-wrapper">
                <div >
                  {isTemplateComment ? (
                    <div className="comment-area-wrapper">
                      <div className="template-selector">
                        <button className="arrow-button"
                          onClick={handlePrev}
                        >
                          ◀
                        </button>
                        <div className="template-title">{currentTemplate.title}</div>
                        <button className="arrow-button"
                          onClick={handleNext}
                        >
                          ▶
                        </button>
                      </div>

                      <textarea
                        className="comment-area"
                        value={currentTemplate.comment}
                        readOnly
                      />
                    </div>
                  ) : (
                    <div className="comment-area-wrapper">
                      <textarea
                        className="comment-area"
                        placeholder="Add comment..."
                        value={userComment}
                        onChange={(e) => setUserComment(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {showAddAction.layout !== 'schedule_inspection' &&
                  <div className="upload-wrapper">
                    <UserActionsWplFileUpload
                      text="Upload inspection-related documents"
                      asPopup={false}
                      displayFiles={Object.keys(uploadedDocuments).length > 0}
                      didSelectFile={uploadedFile}
                      fullWidth={true}
                      didDeleteFile={deletedFile}
                      files={uploadedDocuments}
                    />
                  </div>
                }

                {showAddAction.layout === 'schedule_inspection' && !isTemplateComment &&
                  <div className="inspection-select-container defect-section">
                    <span className="input-wrapper">
                      <label>Inspection type</label>
                      <select
                        value={inspectionOptions.inspection_type ?? ""}
                        onChange={(e) =>
                          setInspectionOptions((prev) => ({
                            ...prev,
                            inspection_type: e.target.value || null,
                          }))
                        }
                      >
                        <option value="">Not selected</option>
                        <option value="internal">Internal</option>
                        <option value="external">External</option>
                      </select>
                    </span>
                    <span>
                      <label>Inspection method</label>
                      <select
                        value={inspectionOptions.inspection_method ?? ""}
                        onChange={(e) =>
                          setInspectionOptions((prev) => ({
                            ...prev,
                            inspection_method: e.target.value || null,
                          }))
                        }
                      >
                        <option value="">Not selected</option>
                        <option value="ground">Ground</option>
                        <option value="drone">Drone</option>
                      </select>
                    </span>
                  </div>
                }
              </div>
            </div>
          }

          {showAddAction.status && showAddAction.layout === "disposition" &&
            <div>
              <div className="disposition-select-container">
                <span className="input-wrapper">
                  <label>Blade</label>
                  <select
                    value={newDefect.blade || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, blade: e.target.value }))}
                  >
                    <option value="" disabled>Select blade</option>
                    {defect.blade.map(blade =>
                      <option key={blade} value={blade}>{blade}</option>
                    )}
                  </select>
                </span>
                <span>
                  <label>Surface</label>
                  <select
                    value={newDefect.surface || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, surface: e.target.value }))}
                  >
                    <option value="" disabled>Select surface</option>
                    {defect.surface.map(surface =>
                      <option key={surface} value={surface}>{surface}</option>
                    )}
                  </select>
                </span>
                <span>
                  <label>Severity</label>
                  <select
                    value={newDefect.severity || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, severity: e.target.value }))}
                  >
                    <option value="" disabled>Select severity</option>
                    {defect.severity.map(severity =>
                      <option key={severity} value={severity}>{severity}</option>
                    )}
                  </select>
                </span>
              </div>

              <div className="disposition-input-container">
                <span>
                  <label>Defect Type</label>
                  <input
                    type="text"
                    value={newDefect.defect_type || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, defect_type: e.target.value }))}
                  />
                </span>
                <span>
                  <label>Layer</label>
                  <input
                    type="text"
                    value={newDefect.layer || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, layer: e.target.value }))}
                  />
                </span>
                <span>
                  <label>Vertical distance(m)</label>
                  <input
                    type="number"
                    value={newDefect.distance || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, distance: e.target.value }))}
                  />
                </span>
                <span>
                  <label>Repair recommendation</label>
                  <input
                    type="text"
                    value={newDefect.repair_recommendation || ""}
                    onChange={(e) => setNewDefect(prev => ({ ...prev, repair_recommendation: e.target.value }))}
                  />
                </span>
              </div>

              <div className="attachments-disposition-wrapper">
                <textarea
                  placeholder="Add comment..."
                  className="repair-comment"
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                />

                <UserActionsWplFileUpload
                  text="Upload inspection-related documents"
                  asPopup={false}
                  displayFiles={Object.keys(uploadedDocuments).length > 0}
                  didSelectFile={uploadedFile}
                  fullWidth={true}
                  didDeleteFile={deletedFile}
                  files={uploadedDocuments}
                />
              </div>
            </div>
          }

          {showAddAction.status && showAddAction.layout === 'comment' &&
            <div className="btn-container">
              <button
                onClick={() => {
                  submitUserAction(
                    "marked_for_inspection",
                  );
                }}
                className="user-action-btn">
                Add comment
              </button>

              <button
                onClick={() => {
                  submitUserAction(
                    "acceptable_risk",
                  );
                }}
                className="user-action-btn">
                Accept risk
              </button>
            </div>
          }

          {showAddAction.status && showAddAction.layout === 'inspection_report' &&
            <div className="btn-container">
              <button
                disabled={mapPopUpLoading || selectedTurbine.inspection === null}
                onClick={() => {
                  const formattedDate = toStandardWplTimestamp(new Date(targetDate));
                  handleResolveInspection({ turbine_ids: [selectedTurbine.id], inspection_ids: [selectedInspectionId], inspection_date: formattedDate, user_comment: userComment })
                }}
                className="user-action-btn">
                Resolve inspection
              </button>
              <button
                disabled={mapPopUpLoading || selectedTurbine.inspection === null || Object.keys(uploadedDocuments).length === 0}
                onClick={() => {
                  handleUploadInspectionData({
                    turbine_id: selectedTurbine.id,
                    inspection_id: selectedInspectionId,
                    file_names: Object.keys(uploadedDocuments),
                    file_datas: Object.keys(uploadedDocuments).map(
                      (k) => uploadedDocuments[k]
                    ),
                    target_date: targetDate,
                    user_comment: userComment.trim() === "" ? null : userComment,
                  });
                }}
                className="user-action-btn"
              >
                Upload inspection data
              </button>
            </div>
          }

          {showAddAction.status && showAddAction.layout === 'disposition' &&
            <div className="btn-container">
              <button
                disabled={
                  mapPopUpLoading ||
                  selectedTurbine.inspection === null ||
                  Object.keys(uploadedDocuments).length === 0 ||
                  isDefectIncomplete
                }
                onClick={() => handleCreateDefect({
                  turbine_id: selectedTurbine.id, inspection_id: selectedInspectionId,
                  user_comment: userComment.trim() === "" ? null : userComment,
                  image_source: 'local',
                  defect_image_data: uploadedDocuments[Object.keys(uploadedDocuments)[0]],
                  blade_image_data: uploadedDocuments[Object.keys(uploadedDocuments)[0]],

                })}
                className="user-action-btn">
                Create defect
              </button>
              <button
                disabled={mapPopUpLoading || selectedTurbine.inspection === null}
                onClick={() => {
                  const formattedDate = toStandardWplTimestamp(new Date(targetDate));
                  handleResolveInspection({
                    turbine_ids: [selectedTurbine.id], inspection_ids: [selectedInspectionId], inspection_date: formattedDate,
                    user_comment: userComment.trim() === "" ? null : userComment,
                  })

                }}
                className="user-action-btn">
                Resolve inspection
              </button>
            </div>
          }

          {showAddAction.status && showAddAction.layout === 'schedule_inspection' &&
            <div className="btn-container">
              <button
                disabled={mapPopUpLoading}
                onClick={() => {
                  const formattedDate = new Date(targetDate).toISOString().split('T')[0];
                  const inspection_type = isTemplateComment
                    ? selectedTemplate?.inspection_type ?? null
                    : inspectionOptions.inspection_type;

                  const inspection_method = isTemplateComment
                    ? selectedTemplate?.inspection_method ?? null
                    : inspectionOptions.inspection_method;

                  handleScheduleInspections({
                    turbine_ids: [selectedTurbine.id],
                    inspection_date: formattedDate,
                    user_comment: userComment.trim() === "" ? null : userComment,
                    inspection_type,
                    inspection_method,
                  });
                }}
                className="user-action-btn">
                Schedule inspection
              </button>
            </div>
          }


          {!showAddAction.status &&
            <div className="btn-container">
              <button onClick={() => setShowAddAction({ status: true, layout: 'comment' })} className="user-action-btn">
                Comment
              </button>
              <button
                disabled={selectedTurbine.inspection === null}
                onClick={() => setShowAddAction({ status: true, layout: "inspection_report" })} className="user-action-btn">
                Inspection report
              </button>
              <button
                disabled={selectedTurbine.inspection === null}
                onClick={() => setShowAddAction({ status: true, layout: "disposition" })} className="user-action-btn">
                Disposition
              </button>
              <button
                onClick={() => {
                  setShowAddAction({ status: true, layout: 'schedule_inspection' });
                  setTargetDate(
                    selectedTurbine.inspection?.date
                      ? toPrettyDateStr(new Date(selectedTurbine.inspection.date))
                      : toPrettyDateStr(new Date())
                  );
                }}
                className="user-action-btn">
                Schedule inspection
              </button>
            </div>
          }
        </div >
      )
      }
    </>
  );
}
